import React, { useState } from 'react';
import { AlertTriangle, ScrollText } from 'lucide-react';
import { Button } from '@/components/ui/button';
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
} from '@/components/ui/dialog';
import { useClinic } from '@/contexts/ClinicContext';
import { useAuth } from '@/contexts/AuthContext';
import { useToast } from '@/hooks/use-toast';
import { clinicService } from '@/services/clinicService';
import { AcknowledgementStep } from '@/pages/clinic-onboarding/steps/AcknowledgementStep';

export const ProgramAgreementBanner: React.FC = () => {
  const { clinic, isAdmin, isGuided, setClinic } = useClinic();
  const { user } = useAuth();
  const { toast } = useToast();
  const [dialogOpen, setDialogOpen] = useState(false);

  const isHybrid = clinic?.clinic_archetype === 'hybrid';

  // Only show for guided/hybrid clinics that haven't acknowledged
  if (!clinic || clinic.program_acknowledged_at || (!isGuided && !isHybrid)) {
    return null;
  }

  // Only admins can acknowledge
  if (!isAdmin) {
    return null;
  }

  return (
    <>
      <div className="mb-6 flex items-center gap-3 rounded-lg border border-amber-200 bg-amber-50 px-4 py-3">
        <AlertTriangle className="h-5 w-5 text-amber-600 flex-shrink-0" />
        <div className="flex-1">
          <p className="text-sm font-medium text-amber-900">
            Program Agreement Not Acknowledged
          </p>
          <p className="text-xs text-amber-700 mt-0.5">
            This {clinic.clinic_archetype} clinic has not acknowledged the Nimbus Providers program terms. Acknowledgement is required for compliance.
          </p>
        </div>
        <Button
          variant="outline"
          size="sm"
          className="shrink-0 border-amber-300 text-amber-800 hover:bg-amber-100"
          onClick={() => setDialogOpen(true)}
        >
          <ScrollText className="h-4 w-4 mr-1.5" />
          Review &amp; Acknowledge
        </Button>
      </div>

      <Dialog open={dialogOpen} onOpenChange={setDialogOpen}>
        <DialogContent className="max-w-2xl max-h-[90vh] overflow-hidden flex flex-col">
          <DialogHeader>
            <DialogTitle>Program Agreement</DialogTitle>
          </DialogHeader>
          <div className="flex-1 overflow-y-auto">
            <AcknowledgementStep
              data={{ clinicArchetype: clinic.clinic_archetype || 'direct' } as any}
              updateData={() => {}}
              onAcknowledged={async () => {
                try {
                  const res = await clinicService.acknowledgeProgram(clinic.id);
                  setDialogOpen(false);
                  if (res?.data?.clinic) {
                    setClinic(res.data.clinic);
                  }
                  toast({
                    title: 'Agreement Acknowledged',
                    description: 'Program terms have been acknowledged for this clinic.',
                    variant: 'success',
                  });
                } catch {
                  toast({
                    title: 'Error',
                    description: 'Failed to save acknowledgement. Please try again.',
                    variant: 'destructive',
                  });
                }
              }}
            />
          </div>
        </DialogContent>
      </Dialog>
    </>
  );
};
